import React, { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import ProspectsTable from './components/ProspectsTable';
import ProspectTodayView from './components/ProspectTodayView';
import ProspectFormDialog from './components/ProspectFormDialog';
import ProspectMessageDialog from './components/ProspectMessageDialog';
import ProspectBulkMessageDialog from './components/ProspectBulkMessageDialog';
import ProspectSelectionBar from './components/ProspectSelectionBar';
import ProspectTemplatesDialog from './components/ProspectTemplatesDialog';
import ProspectSearchDialog from './components/ProspectSearchDialog';
import ProspectFollowersDialog from './components/ProspectFollowersDialog';
import useProspects from './hooks/useProspects';
import { CATEGORY_OPTIONS, STATUS_OPTIONS, SOURCE_OPTIONS } from './prospectOptions';

const EMPTY_FILTERS = { q: '', category: '', status: '', source: '' };
const outlinedSx = { borderRadius: '8px', textTransform: 'none', fontWeight: 600 };

/**
 * AdminProspects - Prospecção (Admin Geral)
 *
 * Catálogo de profissionais a prospectar, com a mensagem pronta por categoria.
 * "Para hoje" é a fila do dia (quem respondeu, retorno vencido, encerrar, novos até
 * a cota); "Todos" é o catálogo com filtros. Um contato por vez ou vários (lote pelo
 * número da Gabriela, com intervalo, nunca pela fila automática); cada envio fica
 * registrado. Regras: docs/features/admin-prospects/README.md
 */
function AdminProspects() {
  const [view, setView] = useState('today');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { items, isLoading, error, sentToday, refresh, upsert, bumpSentToday } = useProspects(
    view === 'today' ? { ...EMPTY_FILTERS, view } : { ...filters, view: '' },
  );
  const [editing, setEditing] = useState(undefined);
  const [messaging, setMessaging] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulk, setBulk] = useState(null); // { prospects, distribution }
  const [templatesOpen, setTemplatesOpen] = useState(false);

  // Só conta quem continua na lista filtrada e tem WhatsApp
  const selected = useMemo(
    () => items.filter((item) => item.whatsapp && selectedIds.includes(item.id)),
    [items, selectedIds],
  );

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  }, []);

  const toggleAll = (checked) => {
    setSelectedIds(checked ? items.filter((item) => item.whatsapp).map((item) => item.id) : []);
  };

  // Um contato só abre a mensagem um a um (a versão já vem certa: Retorno para quem já recebeu)
  const openBulk = useCallback((prospects, distribution) => {
    if (prospects.length === 1) {
      setMessaging(prospects[0]);
      return;
    }
    setBulk({ prospects, distribution });
  }, []);

  const handleSendSelected = () => openBulk(selected, 'split');

  // Cada envio que deu certo entra na cota do dia (o lote chama a cada contato)
  const handleSent = useCallback((saved) => {
    upsert(saved);
    bumpSentToday(1);
  }, [upsert, bumpSentToday]);

  const handleImported = (imported, skipped) => {
    [...imported].reverse().forEach(upsert);
    setNotice(`${imported.length} contato(s) adicionado(s) como Novo.${skipped ? ` ${skipped} já estava(m) no catálogo.` : ''}`);
    setSearchOpen(false);
    setFollowersOpen(false);
  };

  const handleViewChange = (e, value) => {
    if (!value) return;
    setView(value);
    setSelectedIds([]);
  };

  const setFilter = (field) => (e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Prospecção"
          Icon={<PersonSearchOutlinedIcon />}
          Description="Profissionais para chamar. 'Para hoje' junta quem respondeu, quem está sem resposta há 3 dias (sai o Retorno), quem encerrar depois de 7 dias e os novos dentro da cota do dia. A mensagem já vem pronta pela categoria e cada envio fica registrado; até 30 por dia, com intervalo entre um e outro."
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <ToggleButtonGroup value={view} exclusive size="small" onChange={handleViewChange}>
          <ToggleButton value="today" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}>Para hoje</ToggleButton>
          <ToggleButton value="all" sx={{ px: 2, textTransform: 'none', fontWeight: 600 }}>Todos</ToggleButton>
        </ToggleButtonGroup>
        {view === 'today' ? (
          <Tooltip title="Buscar respostas novas">
            <span>
              <IconButton size="small" onClick={refresh} disabled={isLoading} aria-label="Atualizar a fila">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <>
            <TextField size="small" label="Buscar" placeholder="Nome, @, telefone ou e-mail" value={filters.q} onChange={setFilter('q')} sx={{ minWidth: 240 }} />
            <TextField size="small" select label="Categoria" value={filters.category} onChange={setFilter('category')} sx={{ minWidth: 170 }}>
              <MenuItem value="">Todas</MenuItem>
              {CATEGORY_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Situação" value={filters.status} onChange={setFilter('status')} sx={{ minWidth: 150 }}>
              <MenuItem value="">Todas</MenuItem>
              {STATUS_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Origem" value={filters.source} onChange={setFilter('source')} sx={{ minWidth: 150 }}>
              <MenuItem value="">Todas</MenuItem>
              {SOURCE_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              Contatos: <strong>{items.length}</strong>
            </Typography>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<NotesOutlinedIcon fontSize="small" />} onClick={() => setTemplatesOpen(true)} sx={outlinedSx}>
          Textos das mensagens
        </Button>
        <Button variant="outlined" startIcon={<TravelExploreOutlinedIcon fontSize="small" />} onClick={() => setSearchOpen(true)} sx={outlinedSx}>
          Buscar contatos
        </Button>
        <Button variant="outlined" startIcon={<InstagramIcon fontSize="small" />} onClick={() => setFollowersOpen(true)} sx={outlinedSx}>
          Importar seguidores
        </Button>
        <Button variant="contained" startIcon={<AddIcon fontSize="small" />} onClick={() => setEditing(null)} sx={outlinedSx}>
          Novo contato
        </Button>
      </Box>

      {notice && (
        <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      )}

      {view === 'today' ? (
        <ProspectTodayView
          items={items}
          isLoading={isLoading}
          error={error}
          sentToday={sentToday}
          onMessage={setMessaging}
          onEdit={setEditing}
          onBulk={openBulk}
          onUpdated={upsert}
          onNotice={setNotice}
        />
      ) : (
        <>
          <ProspectSelectionBar count={selected.length} onSend={handleSendSelected} onClear={() => setSelectedIds([])} />
          <ProspectsTable
            items={items}
            isLoading={isLoading}
            error={error}
            selectedIds={selectedIds}
            onToggle={toggleSelected}
            onToggleAll={toggleAll}
            onMessage={setMessaging}
            onEdit={setEditing}
          />
        </>
      )}

      <ProspectFormDialog
        isOpen={editing !== undefined}
        prospect={editing || null}
        onSaved={(saved) => {
          upsert(saved);
          setEditing(undefined);
        }}
        onCancel={() => setEditing(undefined)}
      />

      <ProspectMessageDialog
        isOpen={!!messaging}
        prospect={messaging}
        onLogged={(saved) => {
          handleSent(saved);
          setMessaging(null);
          setSelectedIds((prev) => prev.filter((id) => id !== saved?.id));
        }}
        onCancel={() => setMessaging(null)}
      />

      <ProspectBulkMessageDialog
        isOpen={!!bulk}
        prospects={bulk ? bulk.prospects : []}
        initialDistribution={bulk ? bulk.distribution : 'split'}
        onSent={handleSent}
        onEditTemplates={() => setTemplatesOpen(true)}
        onClose={(finished) => {
          // Esc com os textos abertos por cima fecha só os textos
          if (templatesOpen) return;
          setBulk(null);
          if (finished) setSelectedIds([]);
        }}
      />

      {/* Depois do lote: abre por cima dele quando chamado de lá */}
      <ProspectTemplatesDialog
        isOpen={templatesOpen}
        onSaved={() => {
          setTemplatesOpen(false);
          setNotice('Textos das mensagens salvos neste navegador.');
        }}
        onCancel={() => setTemplatesOpen(false)}
      />

      <ProspectSearchDialog
        isOpen={searchOpen}
        onImported={handleImported}
        onCancel={() => setSearchOpen(false)}
      />

      <ProspectFollowersDialog
        isOpen={followersOpen}
        onImported={handleImported}
        onCancel={() => setFollowersOpen(false)}
      />
    </Box>
  );
}

export default AdminProspects;
